import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Paper, Theme, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  useGetTaxonomyPreferencesQuery,
  useSetTaxonomyPreferencesMutation,
} from 'src/store/cmsService'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import { useDeviceType } from '../../rsmCoreComponents/hooks/useDeviceType'
import { Styles } from '../../types'
import SectionHeader from '../layouts/SectionHeader'
import ProfileModal from './ProfileModal'
import { setPopoverContent } from '../../store/form/formSlice'
import IndustriesIconMapper from './ProfileModal/IndustriesIconMapper'
import { Interest, TagIconMap } from './types'
import InterestsSection from './InterestsSection'
import ServiceLinesIconMapper from './ProfileModal/ServiceLinesIconMapper'
import TopicsIconMapper from './ProfileModal/TopicsIconMapper'

const styles: Styles = {
  interestsSection: (theme) => ({
    [theme.breakpoints.up('tablet')]: {
      marginLeft: '1rem',
      gridColumn: 'col 10 / span 4',
      gridRow: 'row 1 / span 1',
      maxWidth: '29.375rem',
      minWidth: '29.375rem',
    },
    [theme.breakpoints.only('mobile')]: {
      gridColumn: 'col 1 / span 12',
    },
  }),
  noInterestsMessage: {
    paddingBottom: '1.875rem',
  },
}

interface InterestsContainerProps {
  reloadKey?: string | undefined | null
}

const InterestsContainer = ({
  reloadKey = undefined,
}: InterestsContainerProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isDesktop } = useDeviceType()
  const [open, setOpen] = useState(false)
  const getTaxonomyPreferences = useGetTaxonomyPreferencesQuery()
  const [setTaxonomyPreferences] = useSetTaxonomyPreferencesMutation()

  const saveAllAndCloseDialog = (data: number[]) => {
    setOpen(false)
    setTaxonomyPreferences(data)
  }

  const closeDialog = useCallback(async () => {
    setOpen(false)
    navigate(`/profile`)
  }, [setOpen, dispatch, navigate, setPopoverContent, t])

  const industriesTagIconMap: TagIconMap = IndustriesIconMapper()
  const industryInterests = getTaxonomyPreferences.data?.data
    ?.filter((item) => item.category?.toLowerCase() === 'industry')
    ?.map((interest) => {
      const tag = interest.tag?.split('/')?.pop()
      return tag ? { ...interest, icon: industriesTagIconMap[tag] } : interest
    }) as Interest[]

  const serviceLinesTagIconMap: TagIconMap = ServiceLinesIconMapper()
  const serviceLineInterests = getTaxonomyPreferences.data?.data
    ?.filter((item) => item.category?.toLowerCase() === 'service')
    ?.map((interest) => {
      const tag = interest.tag?.split('/')?.pop()
      return tag ? { ...interest, icon: serviceLinesTagIconMap[tag] } : interest
    }) as Interest[]

  const topicsTagIconMap: TagIconMap = TopicsIconMapper()
  const topicInterests = getTaxonomyPreferences.data?.data
    ?.filter((item) => item.category?.toLowerCase() === 'topic')
    ?.map((interest) => {
      const tag = interest.tag?.split('/')?.pop()
      return tag ? { ...interest, icon: topicsTagIconMap[tag] } : interest
    }) as Interest[]

  useEffect(() => {
    if (reloadKey) getTaxonomyPreferences.refetch()
  }, [reloadKey])

  return (
    <Paper
      component="section"
      sx={styles.interestsSection}
      data-testid="Sec_Profile_UserInterests">
      <SectionHeader
        overridableSxProps={(theme: Theme) => ({
          display: 'flex',
          marginBottom: '0.5rem',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textTransform: 'uppercase',
          '& hr': {
            flexShrink: 1,
            alignSelf: 'flex-end',
            marginBottom: '0.125rem',
            marginLeft: '0.5rem',
            borderColor: theme.palette.primary.main,
            width: '100%',
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            color: undefined,
          },
        })}
        title={t('UserProfile.PageInterestsHeading')}
        testId="Hed_Profile_UserInterests"
      />
      {!getTaxonomyPreferences.data?.data.length ? (
        <>
          <Typography variant="body1" sx={styles.noInterestsMessage}>
            {t('UserProfile.PageNoInterestsMessage')}
          </Typography>
          <Box
            key="interestsButton"
            sx={{ justifySelf: isDesktop ? 'unset' : 'anchor-center' }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(true)}
              data-testid="Btn_Profile_AddInterests">
              {t('UserProfile.PageAddInterestsButton')}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <InterestsSection
            category="Industries"
            interests={industryInterests}
          />
          <InterestsSection
            category="ServiceLines"
            interests={serviceLineInterests}
          />
          <InterestsSection category="Topics" interests={topicInterests} />
          <Box
            key="interestsButton"
            sx={{ justifySelf: isDesktop ? 'unset' : 'anchor-center' }}>
            <Button
              variant="outlined"
              onClick={() => setOpen(true)}
              data-testid="Btn_Profile_InterestsUpdate">
              {t('UserProfile.PageUpdateInterestsButton')}
            </Button>
          </Box>
        </>
      )}
      <ProfileModal
        open={open}
        defaultValue={getTaxonomyPreferences.data?.data.map((r) => r.id) ?? []}
        skipProfile
        onSubmit={saveAllAndCloseDialog}
        onClose={closeDialog}
      />
    </Paper>
  )
}

export default InterestsContainer
